// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import translate from "@insite/client-framework/Translate";
import LazyImage from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import ListItem, { OrderedList, UnorderedList } from "@insite/mobius/Lists";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getFocalPointStylesFromMobius from "@insite/mobius/utilities/getFocalPointStyles";
import { xGoogleAnalyticsState } from "@west/App/Store/Reducers";
import { largeLinkFontSize, viewAllStyle } from "@west/XDL/Utilities/links";
import { gaRichContentLinkClickHandler } from "@west/XDL/Utilities/tracking";
import { domToReact, HTMLReactParserOptions } from "html-react-parser";
import * as React from "react";
import { shallowEqual } from "react-redux";

export const xParserOptions = (xGoogleAnalytics?: any): HTMLReactParserOptions => ({
    replace: node => {
        const { name, children, attribs = { style: "" }, ...rest } = node;
        const { style, class: className, ...otherAttribs } = attribs;

        otherAttribs.className = className;

        if (name && ["h1", "h2", "h3", "h4", "h5", "h6", "body", "p", "legend"].includes(name) && children) {
            return (
                <Typography
                    css={style as any}
                    {...otherAttribs}
                    variant={name as TypographyProps["variant"]}
                    forwardAs="p"
                >
                    {children && domToReact(children, xParserOptions(xGoogleAnalytics))}
                </Typography>
            );
        }
        if (name === "ul") {
            return (
                <UnorderedList css={style as any} {...otherAttribs}>
                    {children && domToReact(children, xParserOptions(xGoogleAnalytics))}
                </UnorderedList>
            );
        }
        if (name === "ol") {
            return (
                <OrderedList css={style as any} {...otherAttribs}>
                    {children && domToReact(children, xParserOptions(xGoogleAnalytics))}
                </OrderedList>
            );
        }
        if (name === "li") {
            return (
                <ListItem css={style as any} {...otherAttribs}>
                    {children && domToReact(children, xParserOptions(xGoogleAnalytics))}
                </ListItem>
            );
        }
        if (name === "img") {
            const { alt, ...remainingAttribs } = otherAttribs;
            return <LazyImage as="span" altText={alt} css={style as any} {...remainingAttribs} />;
        }

        // This is a customization to trigger Ga event on rich content links with google-analytics class
        if (className && (className as string).includes("google-analytics") && name === "a" && xGoogleAnalytics) {
            const arrowStyle = otherAttribs.className
                ? (otherAttribs.className as string).includes("with-arrow")
                    ? viewAllStyle
                    : undefined
                : undefined;
            return (
                <Link
                    css={style as any}
                    id={otherAttribs.name}
                    {...otherAttribs}
                    {...arrowStyle}
                    {...largeLinkFontSize}
                    onClick={() =>
                        gaRichContentLinkClickHandler(
                            xGoogleAnalytics,
                            otherAttribs.href,
                            children && domToReact(children, xParserOptions(xGoogleAnalytics)),
                        )
                    }
                >
                    {children && domToReact(children, xParserOptions(xGoogleAnalytics))}
                </Link>
            );
        }
        if (name === "a") {
            const arrowStyle = otherAttribs.className
                ? (otherAttribs.className as string).includes("with-arrow")
                    ? viewAllStyle
                    : undefined
                : undefined;

            // Added largeLinkFontSize
            return (
                <Link
                    css={style as any}
                    id={otherAttribs.name}
                    {...otherAttribs}
                    {...arrowStyle}
                    {...largeLinkFontSize}
                >
                    {children && domToReact(children, xParserOptions(xGoogleAnalytics))}
                </Link>
            );
        }
        return undefined;
    },
});

export function getFocalPointStyles(
    focalPoint:
        | "topLeft"
        | "topCenter"
        | "topRight"
        | "centerLeft"
        | "center"
        | "centerRight"
        | "bottomLeft"
        | "bottomCenter"
        | "bottomRight",
) {
    return getFocalPointStylesFromMobius(focalPoint);
}
